import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Package, Product, ProductInterface } from '@vendasta/marketplace-packages';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Pipeline } from '@vendasta/sales-common';
import { StageInterface } from '@vendasta/sales-opportunities';
import { DateRange } from '@vendasta/uikit';
import { parseISO } from 'date-fns';
import { Observable, combineLatest, merge, of } from 'rxjs';
import { auditTime, debounceTime, filter, map, shareReplay, startWith, take } from 'rxjs/operators';
import {
  PipelineStatusCheckboxService,
  PipelineStatuses,
} from '../../pipelines/pipeline-status-checkbox/pipeline-status-checkbox.service';
import { PipelineUIFilters } from '../../pipelines/pipeline/pipeline-service.interface';
import { PotentialRevenueService } from '../../pipelines/pipeline/potential-revenue/potential-revenue.service';
import {
  ActualCloseFilterService,
  AssignedDateFilterService,
  CreatedDateFilterService,
  DateFilterServiceInterface,
  ExpectedCloseFilterService,
} from '../date-filters/date-filters.service';
import { SalesTeamOption } from '../filter-options/sales-team-option.model';
import { SalespersonOption } from '../filter-options/salesperson-option.model';
import { PipelineFilterPackageSelectionService } from '../package-selector/pipeline-filter-package-selection.service';
import { PipelineFilterPackageStoreService } from '../package-selector/pipeline-filter-package-store.service';
import { PipelineMultiSelectService } from '../pipeline-multi-select/pipeline-multi-select.service';
import { ProductSelectorService } from '../product-selector/product-selector.service';
import { SalespersonMultiSelectStoreService } from '../salesperson-multi-select/salesperson-multi-select.store.service';
import { SalesTeamMultiSelectStoreService } from '../salesteam-multi-select/sales-team-multi-select-store.service';
import { MultiSelectTag } from '../tag-selector/tag';
import { TagMultiSelectionStoreService } from '../tag-selector/tag-multi-selection.service';
import { MARKET_SELECTOR_SERVICE_TOKEN, MarketSelectorServiceInterface } from '../market-selector/tokens';
import { AccountFilterService } from '../account-filter/account-filter.service';
import { OpportunityTypeFilterService } from '../opportunity-type-filter/opportunity-type-filter.service';
import { OpportunityTypeFilterStoreService } from '../opportunity-type-filter/opportunity-type-filter-store.service';

import { OpportunityType } from '../../sales-opportunities/interface';

export enum queryConstants {
  SALES_PEOPLE = 'salesPeople',
  SALES_TEAMS = 'salesTeams',
  PACKAGES = 'packages',
  OPP_TAGS = 'oppTags',
  ACCT_TAGS = 'acctTags',
  PIPELINE = 'pipeline',
  PRODUCTS = 'products',
  REVENUE_DATE = 'revDate',
  STATUS = 'status',
  ACTUAL_CLOSE = 'actualClose',
  EXPECTED_CLOSE = 'expectedClose',
  CREATED_DATE = 'createdDate',
  ASSIGNED_DATE = 'assignedDate',
  PIPELINE_STAGE = 'pipelineStage',
  MARKET_ID = 'marketId',
  ACCOUNT_GROUP_ID = 'accountGroupId',
  TYPE = 'type',
}

const QUERYPARAM_FILTERS = 'filters';

// There is a race condition that happens in selectedUIFilters$ because we are doing a combineLatest on a large number
// of observables.  This delay is for the auditTime to give the observable time to collect all of the data from every
// stream.
const UI_FILTER_SELECTION_DELAY = 50;

interface LiveListQueryParam {
  paramKey: string;
  listAction$: Observable<any[]>;
  idFieldName: string;
}

interface LiveQueryParam {
  queryKey: string;
  item$: Observable<any>;
}

type UIFilterInputTypes = [
  SalespersonOption[],
  SalesTeamOption[],
  string[],
  Package[],
  Pipeline[],
  StageInterface,
  MultiSelectTag[],
  MultiSelectTag[],
  Product[],
  Date,
  PipelineStatuses,
  DateRange,
  DateRange,
  DateRange,
  DateRange,
  string,
  string,
  OpportunityType[],
];

export interface PipelineTableFilters {
  salespeopleIds: string[];
  packages: Package[];
  products: Product[];
  pipelines: Pipeline[];
  pipelineStage: StageInterface;
  oppTags: string[];
  acctTags: string[];
  monthlyRevenueDate: Date;
  statuses: PipelineStatuses;
  expectedClose: DateRange;
  actualClose: DateRange;
  createdDate: DateRange;
  assignedDate: DateRange;
  marketId: string;
  accountGroupId: string;
  types: OpportunityType[];
}

@Injectable()
export class PipelineFiltersService {
  selectedFiltersListRequestData$: Observable<PipelineTableFilters>;
  selectedUIFilters$: Observable<PipelineUIFilters>;

  private liveParams: Observable<void>[] = [];
  private currentParams: Params = {};

  constructor(
    private readonly salespersonMultiSelectStoreService: SalespersonMultiSelectStoreService,
    private readonly salesTeamMultiSelectStoreService: SalesTeamMultiSelectStoreService,
    private readonly packageStoreService: PipelineFilterPackageStoreService,
    private readonly packageSelectorService: PipelineFilterPackageSelectionService,
    private readonly pipelineMultiSelectService: PipelineMultiSelectService,
    private readonly tagMultiSelectionStoreService: TagMultiSelectionStoreService,
    private readonly productSelectorService: ProductSelectorService,
    private readonly pipelineStatusesStore: PipelineStatusCheckboxService,
    private readonly potentialRevenueService: PotentialRevenueService,
    private readonly expectedCloseService: ExpectedCloseFilterService,
    private readonly actualCloseService: ActualCloseFilterService,
    private readonly createdDateService: CreatedDateFilterService,
    private readonly assignedDateService: AssignedDateFilterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly productAnalytics: ProductAnalyticsService,
    private readonly accountFilterService: AccountFilterService,
    private readonly opportunityTypeFilterService: OpportunityTypeFilterService,
    private readonly opportunityTypeFilterStoreService: OpportunityTypeFilterStoreService,
    @Optional()
    @Inject(MARKET_SELECTOR_SERVICE_TOKEN)
    private readonly marketSelectorService?: MarketSelectorServiceInterface,
  ) {
    const routeParams: Params =
      JSON.parse(this.activatedRoute?.snapshot?.queryParams?.[QUERYPARAM_FILTERS] || null) || {};

    this.setInitialFiltersFromUrl(routeParams);
    this.setupLiveListQueryParameters();
    this.setupLiveQueryParameters();
    this.initializeLiveQueryParameters();

    // This is done to resolve a race condition
    // TODO (SSC-3390): inject the salesperson service without the salesperson preloaded, then pass salesperson through route params
    const salespeopleIds$ = this.getSelectedSalespeopleIds$();
    this.getSalespeopleFromUrl$(salespeopleIds$, routeParams[queryConstants.SALES_PEOPLE] || []).subscribe(
      (ssp) =>
        ssp.length > 0 &&
        this.salespersonMultiSelectStoreService.setSelectedSalespeople(ssp.map((sp) => sp.toOptionValue())),
    );

    this.selectedUIFilters$ = combineLatest([
      salespersonMultiSelectStoreService.selectedSalespeople$.pipe(startWith([])),
      salesTeamMultiSelectStoreService.selectedSalesTeams$.pipe(startWith([])),
      salesTeamMultiSelectStoreService.selectedTeamsSalespeopleIds$.pipe(startWith([])),
      packageSelectorService.selectedPackages$.pipe(startWith([])),
      pipelineMultiSelectService.selectedPipelines$.pipe(startWith([])),
      pipelineMultiSelectService.selectedPipelineStage$.pipe(startWith(<StageInterface>null)),
      tagMultiSelectionStoreService.selectedOppTags$.pipe(startWith([])),
      tagMultiSelectionStoreService.selectedAccountTags$.pipe(startWith([])),
      productSelectorService.selectedProducts$.pipe(startWith([])),
      potentialRevenueService.selectedDate$.pipe(startWith(new Date())),
      pipelineStatusesStore.statuses$,
      createdDateService.dateRange$.pipe(startWith(<DateRange>null)),
      actualCloseService.dateRange$.pipe(startWith(<DateRange>null)),
      expectedCloseService.dateRange$.pipe(startWith(<DateRange>null)),
      assignedDateService.dateRange$.pipe(startWith(<DateRange>null)),
      (marketSelectorService?.selectedMarketId$ || of()).pipe(startWith(null)),
      accountFilterService.selectedAccountGroupId$.pipe(startWith(null)),
      opportunityTypeFilterService.selectedTypes$.pipe(startWith([])),
    ]).pipe(
      auditTime(UI_FILTER_SELECTION_DELAY),
      map(
        ([
          salespeople,
          salesTeams,
          salesTeamSalespeople,
          packages,
          pipelines,
          pipelineStage,
          oppTags,
          acctTags,
          products,
          potRevenueDate,
          statuses,
          createdDateRange,
          actualDateRange,
          expectedDateRange,
          assignedDateRange,
          marketId,
          accountGroupId,
          types,
        ]: UIFilterInputTypes) => {
          const f: PipelineUIFilters = {
            salespeople: salespeople.map((s) => ({ id: s.id, name: s.fullName })),
            salesTeamsIds: salesTeams.map((s) => ({ groupId: s.id, teamName: s.teamName })),
            salesTeamSalespersonIds: salesTeamSalespeople,
            packages: packages,
            pipelines: pipelines,
            pipelineStage: pipelineStage,
            oppTags: oppTags,
            acctTags: acctTags,
            products: products,
            monthlyRevenueDate: potRevenueDate,
            statuses: statuses,
            expectedClose: expectedDateRange,
            actualClose: actualDateRange,
            createdDate: createdDateRange,
            assignedDate: assignedDateRange,
            marketId: marketId,
            accountGroupId: accountGroupId,
            types: types,
          };
          return f;
        },
      ),
      shareReplay(1),
    );

    this.selectedFiltersListRequestData$ = this.getFilters$(
      salespeopleIds$,
      this.packageSelectorService.selectedPackages$,
      this.pipelineMultiSelectService.selectedPipelines$,
      this.pipelineMultiSelectService.selectedPipelineStage$,
      this.tagMultiSelectionStoreService.selectedOppTags$,
      this.tagMultiSelectionStoreService.selectedAccountTags$,
      this.productSelectorService.selectedProducts$,
      this.potentialRevenueService.selectedDate$,
      this.pipelineStatusesStore.statuses$,
      this.expectedCloseService.dateRange$,
      this.actualCloseService.dateRange$,
      this.createdDateService.dateRange$,
      this.assignedDateService.dateRange$,
      this.marketSelectorService?.selectedMarketId$ || of(null),
      this.accountFilterService.selectedAccountGroupId$,
      this.opportunityTypeFilterService?.selectedTypes$ || of([]),
    );

    this.selectedFiltersListRequestData$.pipe(debounceTime(10000)).subscribe((filters) => {
      const selectedFilters = [];

      for (const [filter, value] of Object.entries(filters)) {
        if (value) {
          selectedFilters.push(filter);
        }
      }

      this.productAnalytics.trackEvent('current-pipeline-filters', 'filters', 'set', -1, {
        selected: JSON.stringify(selectedFilters.sort()),
      });
    });
  }

  private setupLiveListQueryParameters(): void {
    [
      {
        paramKey: queryConstants.SALES_PEOPLE,
        listAction$: this.salespersonMultiSelectStoreService.selectedSalespeople$,
        idFieldName: 'id',
      },
      {
        paramKey: queryConstants.SALES_TEAMS,
        listAction$: this.salesTeamMultiSelectStoreService.selectedSalesTeams$,
        idFieldName: 'id',
      },
      {
        paramKey: queryConstants.PACKAGES,
        listAction$: this.packageSelectorService.selectedPackages$,
        idFieldName: 'packageId',
      },
      {
        paramKey: queryConstants.PIPELINE,
        listAction$: this.pipelineMultiSelectService.selectedPipelines$,
        idFieldName: 'id',
      },
      {
        paramKey: queryConstants.ACCT_TAGS,
        listAction$: this.tagMultiSelectionStoreService.selectedAccountTags$,
        idFieldName: 'name',
      },
      {
        paramKey: queryConstants.OPP_TAGS,
        listAction$: this.tagMultiSelectionStoreService.selectedOppTags$,
        idFieldName: 'name',
      },
      {
        paramKey: queryConstants.PRODUCTS,
        listAction$: this.productSelectorService.selectedProducts$,
        idFieldName: 'productId',
      },
      {
        paramKey: queryConstants.TYPE,
        listAction$: this.opportunityTypeFilterService.selectedTypes$,
        idFieldName: 'id',
      },
    ].forEach((llps: LiveListQueryParam) => {
      this.liveParams.push(
        llps.listAction$.pipe(
          map((items) => items.map((l) => l[llps.idFieldName])),
          map((ids) => {
            if (llps.paramKey === queryConstants.TYPE) {
              ids = ids.map((id) => (id === '' ? 'not-specified' : id));
            }
            this.currentParams[llps.paramKey] = ids.length > 0 ? ids : undefined;
          }),
        ),
      );
    });
  }

  // applies a set of filters; idea is top streams are manipulated and should trickle down to update the UI, and service state
  applySavedFiltersFromStorage(filters: PipelineUIFilters): void {
    const salesPeopleOptionValues = filters.salespeople.map((s) => {
      return new SalespersonOption({ id: s.id, name: s.name }).toOptionValue();
    });
    this.salespersonMultiSelectStoreService.setSelectedSalespeople(salesPeopleOptionValues);

    const salesTeamOptionValues = filters.salesTeamsIds.map((s) => {
      return new SalesTeamOption({ groupId: s.groupId, teamName: s.teamName }).toOptionValue();
    });
    this.salesTeamMultiSelectStoreService.setSelectedSalesTeams(salesTeamOptionValues);

    this.packageSelectorService.setPackageSelection(filters.packages || []);
    this.productSelectorService.setProductSelection(filters.products || []);

    this.pipelineMultiSelectService.setPipelineSelection(filters.pipelines || []);
    this.pipelineMultiSelectService.setPipelineStageSelection(filters.pipelineStage || null);
    this.pipelineStatusesStore.storeStatuses(filters.statuses || null);

    this.tagMultiSelectionStoreService.setOppTagsSelection(filters.oppTags);
    this.tagMultiSelectionStoreService.setAccTagsSelection(filters.acctTags);

    if (filters.monthlyRevenueDate) {
      const revDate = new Date(filters.monthlyRevenueDate);
      this.potentialRevenueService.setDate(revDate);
    }

    if (filters.expectedClose) {
      this.setDateRangeForService(this.expectedCloseService, filters.expectedClose);
    } else {
      this.expectedCloseService.clearSelection();
    }

    if (filters.actualClose) {
      this.setDateRangeForService(this.actualCloseService, filters.actualClose);
    } else {
      this.actualCloseService.clearSelection();
    }

    if (filters.createdDate) {
      this.setDateRangeForService(this.createdDateService, filters.createdDate);
    } else {
      this.createdDateService.clearSelection();
    }

    if (filters.assignedDate) {
      this.setDateRangeForService(this.assignedDateService, filters.assignedDate);
    } else {
      this.assignedDateService.clearSelection();
    }
    if (this.marketSelectorService && filters.marketId) {
      this.marketSelectorService.setSelectedMarketId(filters.marketId);
    }
    if (filters.accountGroupId) {
      this.accountFilterService.setAccountGroupId(filters.accountGroupId);
    } else {
      this.accountFilterService.clearAccountGroupFilter();
    }

    this.opportunityTypeFilterService.setTypeSelection(filters.types || []);
  }

  setDateRangeForService(service: DateFilterServiceInterface, dateRangeStrings: DateRange): void {
    const start = new Date(dateRangeStrings.start);
    const end = new Date(dateRangeStrings.end);

    service.setDateRange(start, end);
  }

  getSelectedPipelineStageId(): Observable<string> {
    return this.pipelineMultiSelectService.selectedPipelineStage$.pipe(map((stage) => stage?.stageId));
  }

  private setupLiveQueryParameters(): void {
    [
      { queryKey: queryConstants.REVENUE_DATE, item$: this.potentialRevenueService.selectedDate$ },
      { queryKey: queryConstants.STATUS, item$: this.pipelineStatusesStore.statuses$ },
      { queryKey: queryConstants.EXPECTED_CLOSE, item$: this.expectedCloseService.dateRange$ },
      { queryKey: queryConstants.ACTUAL_CLOSE, item$: this.actualCloseService.dateRange$ },
      { queryKey: queryConstants.CREATED_DATE, item$: this.createdDateService.dateRange$ },
      { queryKey: queryConstants.PIPELINE_STAGE, item$: this.getSelectedPipelineStageId() },
      { queryKey: queryConstants.MARKET_ID, item$: this.marketSelectorService?.selectedMarketId$ || of(null) },
      {
        queryKey: queryConstants.ACCOUNT_GROUP_ID,
        item$: this.accountFilterService.selectedAccountGroupId$ || of(null),
      },
    ].forEach((llps: LiveQueryParam) => {
      this.liveParams.push(
        llps.item$.pipe(
          map((item) => {
            this.currentParams[llps.queryKey] = item || undefined;
          }),
        ),
      );
    });
  }

  private initializeLiveQueryParameters(): void {
    merge(...this.liveParams).subscribe(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { [QUERYPARAM_FILTERS]: JSON.stringify(this.currentParams, (_, v) => v || undefined) },
        queryParamsHandling: 'merge',
      });
    });
  }

  private setInitialFiltersFromUrl(routeParams: Params): void {
    const filterSets = [];

    if (routeParams[queryConstants.SALES_TEAMS]) {
      filterSets.push(
        this.salesTeamMultiSelectStoreService.selectableSalesTeams$.pipe(
          map((sp) =>
            sp.filter((p) => routeParams[queryConstants.SALES_TEAMS]?.includes(p.id)).map((st) => st.toOptionValue()),
          ),
          map((p) => this.salesTeamMultiSelectStoreService.setSelectedSalesTeams(p)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.PACKAGES]) {
      filterSets.push(
        this.packageStoreService.packages$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.PACKAGES]?.includes(p.packageId))),
          map((p) => this.packageSelectorService.setPackageSelection(p)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.PIPELINE]) {
      filterSets.push(
        this.pipelineMultiSelectService.selectablePipelines$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.PIPELINE]?.includes(p.id))),
          map((p) => this.pipelineMultiSelectService.setPipelineSelection(p)),
          take(1),
        ),
      );
    }
    if (routeParams[queryConstants.PIPELINE_STAGE]) {
      filterSets.push(
        this.pipelineMultiSelectService.selectablePipelines$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.PIPELINE]?.includes(p.id))),
          filter((selectedPipelines) => selectedPipelines.length === 1),
          map(
            (singlePipeline) =>
              singlePipeline[0].stages.filter(
                (stage) => routeParams[queryConstants.PIPELINE_STAGE] === stage.stageId,
              )[0],
          ),
          map((p) => this.pipelineMultiSelectService.setPipelineStageSelection(p)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.OPP_TAGS]) {
      filterSets.push(
        this.tagMultiSelectionStoreService.selectableOppTags$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.OPP_TAGS]?.includes(p.name))),
          map((p) => this.tagMultiSelectionStoreService.setOppTagsSelection(p)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.ACCT_TAGS]) {
      filterSets.push(
        this.tagMultiSelectionStoreService.selectableAccTags$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.ACCT_TAGS]?.includes(p.name))),
          map((p) => this.tagMultiSelectionStoreService.setAccTagsSelection(p)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.PRODUCTS])
      filterSets.push(
        this.productSelectorService.products$.pipe(
          map((sp) => sp.filter((p) => routeParams[queryConstants.PRODUCTS]?.includes(p.productId))),
          map((p) => this.productSelectorService.setProductSelection(p)),
          take(1),
        ),
      );

    if (routeParams[queryConstants.TYPE]) {
      filterSets.push(
        this.opportunityTypeFilterStoreService.availableTypes$.pipe(
          map((types) => types.filter((t) => routeParams[queryConstants.TYPE]?.includes(t.id))),
          map((types) => this.opportunityTypeFilterService.setTypeSelection(types)),
          take(1),
        ),
      );
    }

    if (routeParams[queryConstants.ACCOUNT_GROUP_ID]) {
      this.accountFilterService.setAccountGroupId(routeParams[queryConstants.ACCOUNT_GROUP_ID]);
    }

    merge(...filterSets).subscribe();
    this.setDateFiltersFromQueryParams(routeParams);

    const revenueDateFromUrl = routeParams[queryConstants.REVENUE_DATE] || undefined;
    if (revenueDateFromUrl) {
      this.potentialRevenueService.setDate(parseISO(revenueDateFromUrl));
    }

    const initialStatus: PipelineStatuses = { open: true, won: null, lost: null };
    if (routeParams[queryConstants.STATUS]) {
      initialStatus.open = null;
      Object.assign(initialStatus, routeParams[queryConstants.STATUS]);
    }

    if (this.marketSelectorService && routeParams[queryConstants.MARKET_ID]) {
      this.marketSelectorService.setSelectedMarketId(routeParams[queryConstants.MARKET_ID]);
    }

    this.pipelineStatusesStore.storeStatuses(initialStatus);
  }

  applyFiltersFromQueryParams(): void {
    const routeParams = this.activatedRoute?.snapshot?.queryParams;
    if (!routeParams['filters']) {
      return;
    }
    const filters = JSON.parse(routeParams['filters']);
    this.setSalesPeopleFilterFromQueryParams(filters);
    this.setSalesTeamsFilterFromQueryParams(filters);
    this.setOpportunityTypeFilterFromQueryParams(filters);
    this.setProductsFilterFromQueryParams(filters);
    this.setPackagesFilterFromQueryParams(filters);
    this.setTagsFromQueryParams(filters);
    this.setDateFiltersFromQueryParams(filters);
    this.setStatusFromQueryParams(filters);
    this.setAccountFromQueryParams(filters);
  }

  private setSalesPeopleFilterFromQueryParams(filters: object): void {
    const salesPeople = filters[queryConstants.SALES_PEOPLE] || [];
    this.salespersonMultiSelectStoreService
      .getSalesPeopeOptionsFromIds$(salesPeople)
      .pipe(take(1))
      .subscribe((salesPeopleOptions) =>
        this.salespersonMultiSelectStoreService.setSelectedSalespeople(salesPeopleOptions),
      );
  }

  private setSalesTeamsFilterFromQueryParams(filters: object): void {
    // Salesteams
    const salesTeams = filters[queryConstants.SALES_TEAMS] || [];
    this.salesTeamMultiSelectStoreService
      .getSalesTeamOptionsFromIds$(salesTeams)
      .pipe(take(1))
      .subscribe((salesTeamOptions) => this.salesTeamMultiSelectStoreService.setSelectedSalesTeams(salesTeamOptions));
  }

  private setOpportunityTypeFilterFromQueryParams(filters: object): void {
    const types = filters[queryConstants.TYPE] || [];
    this.opportunityTypeFilterStoreService.availableTypes$
      .pipe(
        map((availableTypes) => {
          const ids = types.map((id) => (id === 'not-specified' ? '' : id));
          return availableTypes.filter((t) => ids?.includes(t.id));
        }),
        take(1),
      )
      .subscribe((types) => this.opportunityTypeFilterService.setTypeSelection(types));
  }

  private setProductsFilterFromQueryParams(filters: object): void {
    // Products
    const products = filters[queryConstants.PRODUCTS] || [];
    this.productSelectorService.products$
      .pipe(
        map((availableProducts) => availableProducts.filter((p) => products?.includes(p.productId))),
        take(1),
      )
      .subscribe((selectedProducts) => this.productSelectorService.setProductSelection(selectedProducts));
  }

  private setPackagesFilterFromQueryParams(filters: object): void {
    // Packages
    const packages = filters[queryConstants.PACKAGES] || [];
    this.packageStoreService.packages$
      .pipe(
        map((availablePackages) => availablePackages.filter((p) => packages?.includes(p.packageId))),
        take(1),
      )
      .subscribe((selectedPackages) => this.packageSelectorService.setPackageSelection(selectedPackages));
  }

  private setTagsFromQueryParams(filters: object): void {
    const oppTags = filters[queryConstants.OPP_TAGS] || [];
    const accTags = filters[queryConstants.ACCT_TAGS] || [];
    combineLatest([
      this.tagMultiSelectionStoreService.selectableOppTags$,
      this.tagMultiSelectionStoreService.selectableAccTags$,
    ])
      .pipe(
        map(([availableOppTags, availableAccTags]) =>
          [...availableOppTags, ...availableAccTags].filter((t) => [...oppTags, ...accTags]?.includes(t.name)),
        ),
        take(1),
      )
      .subscribe((selectedTags) => this.tagMultiSelectionStoreService.setTagsSelection(selectedTags));
  }

  private setDateFiltersFromQueryParams(filters: object): void {
    this.setFilterServiceForDateQueryParam(filters[queryConstants.CREATED_DATE] || null, this.createdDateService);
    this.setFilterServiceForDateQueryParam(filters[queryConstants.EXPECTED_CLOSE] || null, this.expectedCloseService);
    this.setFilterServiceForDateQueryParam(filters[queryConstants.ACTUAL_CLOSE] || null, this.actualCloseService);
    this.setFilterServiceForDateQueryParam(filters[queryConstants.ASSIGNED_DATE] || null, this.assignedDateService);
  }

  private setStatusFromQueryParams(filters: object): void {
    this.pipelineStatusesStore.storeStatuses({
      open: filters[queryConstants.STATUS]?.open || false,
      won: filters[queryConstants.STATUS]?.won || false,
      lost: filters[queryConstants.STATUS]?.lost || false,
    });
  }

  private setAccountFromQueryParams(filters: object): void {
    if (filters[queryConstants.ACCOUNT_GROUP_ID]) {
      this.accountFilterService.setAccountGroupId(filters[queryConstants.ACCOUNT_GROUP_ID]);
    }
  }

  private setFilterServiceForDateQueryParam(queryVal: any, dateFilterService: DateFilterServiceInterface): void {
    const dateFromUrl = queryVal || undefined;
    if (dateFromUrl) {
      dateFilterService.setDateRange(parseISO(dateFromUrl?.start), parseISO(dateFromUrl?.end));
    } else {
      dateFilterService.clearSelection();
    }
  }

  // TODO: rather than doing skip while, can we just change the query params on the default route and skip loading sp by default?
  private getSalespeopleFromUrl$(
    block: Observable<string[]>,
    urlSalesPeople: string[],
  ): Observable<SalespersonOption[]> {
    return this.salespersonMultiSelectStoreService.selectableSalespeople$.pipe(
      map((ssp) => ssp.filter((sp) => urlSalesPeople.includes(sp.id))),
    );
  }

  private getSelectedSalespeopleIds$(): Observable<string[]> {
    return combineLatest([
      this.salespersonMultiSelectStoreService.selectedSalespeople$,
      this.salesTeamMultiSelectStoreService.selectedTeamsSalespeopleIds$.pipe(startWith([])),
    ]).pipe(
      map(([selectedSalespeople, salesTeamsPeople]) =>
        this.groupSalespeopleIds(
          salesTeamsPeople,
          selectedSalespeople.map((s) => s.id),
        ),
      ),
    );
  }

  private groupSalespeopleIds(salesTeamsPeople: string[], salespeopleIds: string[]): string[] {
    if (salespeopleIds?.length === 0 && salesTeamsPeople?.length === 0) {
      return [];
    }
    if (salespeopleIds?.length === 0 && salesTeamsPeople.length > 0) {
      return salesTeamsPeople;
    }
    if (salespeopleIds.length > 0 && salesTeamsPeople?.length === 0) {
      return salespeopleIds;
    }

    return salesTeamsPeople.concat(...salespeopleIds);
  }

  private getFilters$(
    teamsAndSalespersonSalespeopleIds$: Observable<string[]>,
    selectedPackages$: Observable<Package[]>,
    selectedPipelines$: Observable<Pipeline[]>,
    selectedPipelineStage$: Observable<StageInterface>,
    selectedOppTags$: Observable<MultiSelectTag[]>,
    selectedAcctTags$: Observable<MultiSelectTag[]>,
    selectedProducts$: Observable<ProductInterface[]>,
    revenueSelectedDate$: Observable<Date>,
    statuses$: Observable<PipelineStatuses>,
    expectedCloseDate$: Observable<DateRange>,
    actualCloseDate$: Observable<DateRange>,
    createdDateRange$: Observable<DateRange>,
    assignedDateRange$: Observable<DateRange>,
    marketId$: Observable<string>,
    accountGroupId$: Observable<string>,
    selectedTypes$: Observable<OpportunityType[]>,
  ): Observable<PipelineTableFilters> {
    return combineLatest([
      teamsAndSalespersonSalespeopleIds$.pipe(startWith(<string[]>[])), // We are guaranteed to at least have the logged in salesperson
      selectedPackages$.pipe(startWith(<Package[]>[])),
      selectedPipelines$.pipe(startWith(<Pipeline[]>[])),
      selectedPipelineStage$.pipe(startWith(<StageInterface>null)),
      selectedOppTags$.pipe(startWith(<MultiSelectTag[]>[])),
      selectedAcctTags$.pipe(startWith(<MultiSelectTag[]>[])),
      selectedProducts$.pipe(startWith(<ProductInterface[]>[])),
      revenueSelectedDate$.pipe(startWith(<DateRange>null)),
      statuses$, // The status store is initialized with a default value after checking query params
      expectedCloseDate$.pipe(startWith(<DateRange>null)),
      actualCloseDate$.pipe(startWith(<DateRange>null)),
      createdDateRange$.pipe(startWith(<DateRange>null)),
      assignedDateRange$.pipe(startWith(<DateRange>null)),
      marketId$.pipe(startWith(<string>null)),
      accountGroupId$.pipe(startWith(<string>null)),
      selectedTypes$.pipe(startWith(<OpportunityType[]>[])),
    ]).pipe(
      map(
        ([
          salespeople,
          packages,
          pipelines,
          pipelineStage,
          oppTags,
          accountTags,
          products,
          potentialRevenueDate,
          statuses,
          expectedCloseRange,
          actualCloseRange,
          createdDateRange,
          assignedDateRange,
          marketId,
          accountGroupId,
          types,
        ]: [
          string[],
          Package[],
          Pipeline[],
          StageInterface,
          MultiSelectTag[],
          MultiSelectTag[],
          ProductInterface[],
          Date,
          PipelineStatuses,
          DateRange,
          DateRange,
          DateRange,
          DateRange,
          string,
          string,
          OpportunityType[],
        ]) => {
          return <PipelineTableFilters>{
            salespeopleIds: salespeople.length === 0 ? null : salespeople,
            pipelines: pipelines.length === 0 ? null : pipelines,
            pipelineStage: pipelineStage,
            packages: packages.length === 0 ? null : packages,
            oppTags: oppTags.length === 0 ? null : oppTags.map((t) => t.name),
            acctTags: accountTags.length === 0 ? null : accountTags.map((t) => t.name),
            products: products.length === 0 ? null : products,
            monthlyRevenueDate: potentialRevenueDate,
            statuses: statuses,
            expectedClose: expectedCloseRange,
            actualClose: actualCloseRange,
            createdDate: createdDateRange,
            assignedDate: assignedDateRange,
            marketId: marketId,
            accountGroupId: accountGroupId,
            types: types.length === 0 ? null : types,
          };
        },
      ),
      shareReplay(1),
    );
  }
}
